import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EditorLoading } from "./shared/EditorLoading";
import { EditorForm } from "./shared/EditorForm";
import { EditorField } from "./shared/EditorField";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  html_template: string;
}

interface TemplateEditorCardProps {
  tpl: EmailTemplate;
  isSubmitting: boolean;
  onSave: (payload: { id: string; subject: string; html_template: string }) => Promise<void>;
}

function TemplateEditorCard({ tpl, isSubmitting, onSave }: TemplateEditorCardProps) {
  const [htmlValue, setHtmlValue] = useState<string>(tpl.html_template);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="capitalize">{tpl.name.replace(/_/g, ' ')}</CardTitle>
      </CardHeader>
      <CardContent>
        <EditorForm
          isSubmitting={isSubmitting}
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.currentTarget as HTMLFormElement;
            const formData = new FormData(form);
            const subject = String(formData.get("subject") || "");
            await onSave({ id: tpl.id, subject, html_template: htmlValue });
          }}
        >
          <EditorField label="Subject" name="subject" defaultValue={tpl.subject} required />
          <div>
            <Label className="text-sm font-medium">HTML Template</Label>
            <div className="mt-1 rounded-md border border-input">
              <ReactQuill theme="snow" value={htmlValue} onChange={setHtmlValue} />
            </div>
          </div>
          <div className="text-sm text-muted-foreground">
            Tip: Click Save Changes, then send a test invite to preview rendering.
          </div>
        </EditorForm>
      </CardContent>
    </Card>
  );
}

export const EmailTemplatesEditor = () => {
  const qc = useQueryClient();
  const [submittingId, setSubmittingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("id, name, subject, html_template")
        .order("name", { ascending: true });
      if (error) throw error;
      return (data ?? []) as EmailTemplate[];
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: { id: string; subject: string; html_template: string }) => {
      const { id, subject, html_template } = payload;
      const { error } = await supabase
        .from("email_templates")
        .update({ subject, html_template })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["email-templates"] }),
  });

  if (isLoading) return <EditorLoading />;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="bg-[#0A0F29] text-white p-6 rounded-lg">
        <h1 className="text-2xl font-bold mb-1 text-[#00BFA6]">Email Templates</h1>
        <p className="text-gray-300">Edit invitation email subjects and HTML content. Use placeholders below.</p>
      </div>

      <Alert>
        <AlertDescription>
          Placeholders you can use: {"{{email}}"}, {"{{password}}"} (new users only), {"{{portal_url}}"}
        </AlertDescription>
      </Alert>

      <div className="grid grid-cols-1 gap-6">
        {data?.map((tpl) => (
          <TemplateEditorCard
            key={tpl.id}
            tpl={tpl}
            isSubmitting={submittingId === tpl.id || updateMutation.isPending}
            onSave={async ({ id, subject, html_template }) => {
              setSubmittingId(id);
              try {
                await updateMutation.mutateAsync({ id, subject, html_template });
              } finally {
                setSubmittingId(null);
              }
            }}
          />
        ))}
      </div>
    </div>
  );
};
